import { useParams, Link } from 'react-router-dom';
import { featuredArticles, fullArticles, categoryArticles } from '@/mocks/data';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const article = id ? fullArticles[id] : null;

  if (!article) {
    const fallback = featuredArticles.find((a) => a.id === id);
    if (fallback) {
      const breadcrumbItems = [
        { label: '首页', href: '/' },
        { label: fallback.category, href: `/category/${fallback.categorySlug}` },
        { label: fallback.title },
      ];

      return (
        <>
          <SEOMeta
            title={`${fallback.title} - 91麻豆`}
            description={fallback.excerpt}
            keywords={fallback.tags.join(',')}
            ogType="article"
          />
          <div className="pt-20 bg-background-50 min-h-screen">
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
              <Breadcrumb items={breadcrumbItems} />
              <article>
                <header className="mb-8">
                  <div className="flex items-center gap-2 mb-3">
                    <Link
                      to={`/category/${fallback.categorySlug}`}
                      className="px-3 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700 whitespace-nowrap cursor-pointer"
                    >
                      {fallback.category}
                    </Link>
                    <span className="text-sm text-foreground-400">{fallback.date}</span>
                  </div>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                    {fallback.title}
                  </h1>
                  <div className="flex items-center gap-3 text-sm text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-user-line"></i>
                      {fallback.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {fallback.views.toLocaleString()} 次浏览
                    </span>
                  </div>
                </header>
                <div className="aspect-[2/1] rounded-lg overflow-hidden mb-8">
                  <img
                    src={fallback.image}
                    alt={fallback.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="prose max-w-none">
                  <p className="text-foreground-600 leading-relaxed text-base">
                    {fallback.excerpt}
                  </p>
                  <p className="text-foreground-500 mt-4 text-sm">
                    本文详细内容即将发布，敬请期待。如需了解更多{fallback.category}相关资讯，请浏览我们的
                    <Link to={`/category/${fallback.categorySlug}`} className="text-primary-500 hover:text-primary-600 cursor-pointer">
                      {fallback.category}频道
                    </Link>
                    。
                  </p>
                </div>
                <div className="mt-6 flex items-center gap-2 flex-wrap">
                  {fallback.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-600 whitespace-nowrap"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </article>
            </div>
          </div>
        </>
      );
    }
    return (
      <div className="pt-20 flex items-center justify-center min-h-screen">
        <p className="text-foreground-500">文章未找到</p>
      </div>
    );
  }

  const relatedArticles = article.relatedIds
    .map((rid) => {
      for (const catArticles of Object.values(categoryArticles)) {
        const found = catArticles.find((a) => a.id === rid);
        if (found) return found;
      }
      return null;
    })
    .filter(Boolean) as typeof categoryArticles[string];

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: article.category, href: `/category/${article.categorySlug}` },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    image: article.image,
    description: article.content.substring(0, 160),
  };

  return (
    <>
      <SEOMeta
        title={`${article.title} - 91麻豆`}
        description={article.content.substring(0, 160)}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://clinicvip.cn/article/${id}`}
        ogType="article"
        schemaData={articleSchema}
      />

      <div className="pt-20 bg-background-50 min-h-screen">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb items={breadcrumbItems} />

          <article>
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-3">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="px-3 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700 whitespace-nowrap cursor-pointer"
                >
                  {article.category}
                </Link>
                <span className="text-sm text-foreground-400">{article.date}</span>
              </div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                {article.title}
              </h1>
              <div className="flex items-center gap-3 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.views.toLocaleString()} 次浏览
                </span>
              </div>
            </header>

            <div className="aspect-[2/1] rounded-lg overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="prose max-w-none">
              {article.content.split('\n\n').map((paragraph, i) => (
                <p key={i} className="text-base text-foreground-700 leading-relaxed mb-4">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-6 flex items-center gap-2 flex-wrap">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-600 whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="mt-12 pt-8 border-t border-background-200/70">
              <h3 className="text-lg font-heading font-bold text-foreground-900 mb-5">
                相关推荐
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="aspect-[16/10] rounded-lg overflow-hidden mb-3">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <h4 className="text-sm font-medium text-foreground-800 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                      {ra.title}
                    </h4>
                    <span className="text-xs text-foreground-400 mt-1">{ra.date}</span>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
}